export enum UserRoles {
  ADMIN = 'admin',
  TECHNICAL = 'technical',
  MANAGER = 'manager',
  PRODUCER = 'producer',
  USER = 'user',
}

export enum WeekDays {
  sunday = 0,
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6,
}

export enum SensorType {
  ELECTRICITY = 'electricity',
  ROTATION = 'rotation',
  TEMPERATURE = 'temperature',
  DOOR_OPEN = 'door_open',
}

export enum MaintenanceTypeCategory {
  PREVENTIVE = 'preventive',
  CORRECTIVE = 'corrective',
  PREDICTIVE = 'predictive',
}

export enum MaintenanceTypeArea {
  MECHANIC = 'mechanic',
  ELECTRICAL = 'electrical',
  HYDRAULIC = 'hydraulic',
  AUTOMATION = 'automation',
  BEM = 'bem',
  REFRIGERATION = 'refrigeration',
}

export function translateSensorType(sensorType: SensorType): string {
  const translations: Record<SensorType, string> = {
    [SensorType.ELECTRICITY]: 'Energia',
    [SensorType.ROTATION]: 'Rotação',
    [SensorType.TEMPERATURE]: 'Temperatura',
    [SensorType.DOOR_OPEN]: 'Abertura',
  }
  return translations[sensorType] || 'Desconhecido'
}

export function translateSensorStatus(status: string): string {
  const translations: Record<string, string> = {
    'activated': 'Ativado',
    'active': 'Ativo',
    'inactive': 'Inativo',
    'deactivated': 'Desativado',
    'pending': 'Pendente',
    'error': 'Erro',
    'maintenance': 'Manutenção',
  }
  return translations[status?.toLowerCase()] || status || 'Desconhecido'
}

export function getSensorColor(sensorType: SensorType): string {
  const colors: Record<SensorType, string> = {
    [SensorType.ELECTRICITY]: 'bg-blue',
    [SensorType.ROTATION]: 'bg-yellow-400',
    [SensorType.TEMPERATURE]: 'bg-darkGreen',
    [SensorType.DOOR_OPEN]: 'bg-orange-500',
  }

  return colors[sensorType] || 'bg-slate-400'
}

/** Cor sólida (hex) para bordas/indicadores — alinhada à paleta de `getSensorColor`. */
export function getSensorTypeBorderColor(sensorType: SensorType): string {
  const colors: Record<SensorType, string> = {
    [SensorType.ELECTRICITY]: '#123ADD',
    [SensorType.ROTATION]: '#facc15',
    [SensorType.TEMPERATURE]: '#099657',
    [SensorType.DOOR_OPEN]: '#f97316',
  }

  return colors[sensorType] || '#94a3b8'
}

export function translateMaintenanceCategory(category: MaintenanceTypeCategory): string {
  const translations: Record<MaintenanceTypeCategory, string> = {
    [MaintenanceTypeCategory.PREVENTIVE]: 'Preventiva',
    [MaintenanceTypeCategory.CORRECTIVE]: 'Corretiva',
    [MaintenanceTypeCategory.PREDICTIVE]: 'Preditiva',
  }
  return translations[category] || 'Desconhecida'
}

export function translateMaintenanceArea(area: MaintenanceTypeArea): string {
  const translations: Record<MaintenanceTypeArea, string> = {
    [MaintenanceTypeArea.MECHANIC]: 'Mecânica',
    [MaintenanceTypeArea.ELECTRICAL]: 'Elétrica',
    [MaintenanceTypeArea.HYDRAULIC]: 'Hidráulica',
    [MaintenanceTypeArea.AUTOMATION]: 'Automação e Controle',
    [MaintenanceTypeArea.BEM]: 'Sistema BEM',
    [MaintenanceTypeArea.REFRIGERATION]: 'Refrigeração',
  }
  return translations[area] || 'Desconhecida'
}

export function translateUserRole(role?: string): string {
  const translations: Record<string, string> = {
    [UserRoles.ADMIN]: 'Administrador',
    [UserRoles.TECHNICAL]: 'Técnico',
    [UserRoles.MANAGER]: 'Gestor',
    [UserRoles.PRODUCER]: 'Produtor',
    [UserRoles.USER]: 'Usuário',
  }

  return translations[role || ''] || role || 'Não informado'
}

export function translateBusinessType(type?: string): string {
  const translations: Record<string, string> = {
    extrusion: 'Extrusão',
    refrigeration: 'Refrigeração',
  }

  return translations[type || ''] || type || 'Não informado'
}

export enum ClassificationTypeNature {
  ACCIDENTAL = 'accidental',
  SCHEDULED = 'scheduled',
  NO_DEMAND = 'no_demand',
  PRODUCING = 'producing',
}

export const CLASSIFICATION_TYPE_NATURE_OPTIONS: {
  value: ClassificationTypeNature | ''
  label: string
}[] = [
  { value: '', label: 'Sem natureza' },
  { value: ClassificationTypeNature.ACCIDENTAL, label: 'Acidental' },
  { value: ClassificationTypeNature.SCHEDULED, label: 'Programado' },
  { value: ClassificationTypeNature.NO_DEMAND, label: 'Sem demanda' },
  { value: ClassificationTypeNature.PRODUCING, label: 'Produzindo' },
]

export function getClassificationTypeNatureBadgeClass(nature?: string | null): string {
  const classes: Record<string, string> = {
    [ClassificationTypeNature.ACCIDENTAL]: 'bg-red-100 text-red-700',
    [ClassificationTypeNature.SCHEDULED]: 'bg-green-lighter text-green-dark',
    [ClassificationTypeNature.NO_DEMAND]: 'bg-gray-100 text-gray-600',
    [ClassificationTypeNature.PRODUCING]: 'bg-blue-100 text-blue-700',
  }

  return classes[nature || ''] || 'bg-gray-100 text-gray-600'
}

export function getClassificationTypeNatureLabel(nature?: string | null): string {
  if (!nature) return ''

  return CLASSIFICATION_TYPE_NATURE_OPTIONS.find((option) => option.value === nature)?.label || nature
}

export const ALARM_TYPE_BY_NUMBER: Record<number, string> = {
  0: 'low_current',
  1: 'hight_current',
  2: 'interruption',
  3: 'unrated_cycle',
  4: 'burnt_resistor',
  5: 'defrost_time',
  6: 'critical_temperature',
  7: 'low_load',
  8: 'no_data',
  9: 'outside_regular_time',
  10: 'door_open',
  11: 'activation_peak_hour',
  12: 'interruption_group',
  13: 'power_factor',
  14: 'empty_mixer',
  15: 'power_outage',
  16: 'overvoltage',
  17: 'correlational'
}

export const ALARM_TYPE_LABELS: Record<string, string> = {
  low_current: 'Corrente baixa',
  hight_current: 'Corrente alta',
  interruption: 'Interrupção',
  unrated_cycle: 'Ciclo não nominal',
  burnt_resistor: 'Resistência queimada',
  defrost_time: 'Tempo de degelo inadequado',
  critical_temperature: 'Temperatura crítica',
  low_load: 'Carga baixa',
  no_data: 'Ausência de dados',
  outside_regular_time: 'Funcionamento fora do horário regular',
  door_open: 'Porta aberta',
  activation_peak_hour: 'Ativação em horário de pico',
  interruption_group: 'Interrupção - Grupo',
  power_factor: 'Fator de potência',
  empty_mixer: 'Misturador vazio',
  power_outage: 'Interrupção de energia',
  overvoltage: 'Sobretensão',
  correlational: 'Correlacional'
}

/** Tipos de alarme vinculados a grupo (sem sensor); ver `Alarm` no backend. */
export const GROUP_ONLY_ALARM_TYPES = ['interruption_group', 'power_outage', 'correlational'] as const

export type GroupOnlyAlarmType = (typeof GROUP_ONLY_ALARM_TYPES)[number]

export function isGroupOnlyAlarmType(type: string): type is GroupOnlyAlarmType {
  return (GROUP_ONLY_ALARM_TYPES as readonly string[]).includes(type)
}

export function translateAlarmType(type?: string | number): string {
  if (type === undefined || type === null) return 'Não informado'
  const key =
    typeof type === 'number' ? ALARM_TYPE_BY_NUMBER[type] ?? String(type) : String(type)
  return ALARM_TYPE_LABELS[key] || key
}

/** Pluralização para KPI «Alarmes no período» (ex.: Acompanhamento Diário). */
export function formatAlarmPeriodUnitLabel(count: number): string {
  return count === 1 ? 'alarme' : 'alarmes'
}
